// Shared, schema-driven tabular-import engine.
//
// Parses pasted text / CSV / native .xlsx into a raw array-of-arrays, detects the
// real header row (real-world registers carry a title/legend row above the
// headers), and — given a field *schema* — guesses a column→field mapping and
// builds typed records. The BOM importer (BomImport.jsx) shares the parse
// primitives here; the generic TabularImport component drives mapping/preview
// from a schema so any register (equipment, jigs, …) can be bulk-imported.
import * as XLSX from 'xlsx/dist/xlsx.full.min.js';

// Split one line into cells using the detected delimiter (quote-aware for CSV).
export function splitLine(line, delim) {
  if (delim === '\t') return line.split('\t');
  const out = []; let cur = ''; let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else inQuotes = false; }
      else cur += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === ',') { out.push(cur); cur = ''; }
    else cur += ch;
  }
  out.push(cur);
  return out;
}

// Raw text / CSV → array-of-arrays (no header assumptions yet).
export function textToAoa(text) {
  const lines = String(text || '')
    .replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    .split('\n').filter(l => l.trim() !== '');
  if (!lines.length) return [];
  const delim = lines[0].indexOf('\t') !== -1 ? '\t' : ',';
  return lines.map(l => splitLine(l, delim).map(c => c.trim()));
}

// Parse a workbook once. The caller holds the returned `wb` so it can switch
// sheets (sheetToAoa) without re-reading the file.
export function readWorkbook(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  return { wb, sheetNames: (wb && wb.SheetNames) || [] };
}

// One named worksheet → array-of-arrays (trimmed strings).
export function sheetToAoa(wb, sheetName) {
  const ws = wb && wb.Sheets ? wb.Sheets[sheetName] : null;
  if (!ws) return [];
  return XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '', raw: false })
    .map(r => (r || []).map(c => String(c == null ? '' : c).trim()));
}

// Native .xlsx/.xls workbook → first sheet's array-of-arrays (back-compat helper).
export function workbookToAoa(arrayBuffer) {
  const { wb, sheetNames } = readWorkbook(arrayBuffer);
  return sheetNames.length ? sheetToAoa(wb, sheetNames[0]) : [];
}

// Generic keyword set for header-row detection when a schema supplies none.
export const DEFAULT_HEADER_HINTS = ['part', 'description', 'desc', 'qty', 'quantity',
  'item', 'mfg', 'mfr', 'manufacturer', 'unit', 'uom', 'ref', 'reference', 'designator',
  'component', 'vendor', 'revision', 'pos', 'name', 'serial', 'asset', 'model', 'location',
  'type', 'status', 'date', 'interval', 'number', 'no', 'id', 'owner', 'drawing', 'method'];

function headerScore(row, hints) {
  let s = 0;
  for (const cell of row) {
    const c = String(cell == null ? '' : cell).toLowerCase().replace(/[^a-z0-9]/g, '');
    if (c && hints.some(h => c.includes(h))) s += 1;
  }
  return s;
}

// Pick the most likely header row in the first ~30 rows: a row with ≥2 keyword
// hits wins (most hits, then most filled cells); otherwise the fullest row.
export function detectHeaderRow(aoa, hints = DEFAULT_HEADER_HINTS) {
  const limit = Math.min(aoa.length, 30);
  let bestIdx = 0, best = -1;
  for (let i = 0; i < limit; i++) {
    const row = aoa[i] || [];
    const hits = headerScore(row, hints);
    const filled = row.filter(c => String(c == null ? '' : c).trim() !== '').length;
    const score = hits >= 2 ? hits * 1000 + filled : filled;
    if (score > best) { best = score; bestIdx = i; }
  }
  return bestIdx;
}

// AOA + chosen header row → { headers, rows, headerIndex }. Trims phantom trailing
// columns (empty in the header and every data row) and drops fully-empty rows.
export function aoaToTable(aoa, headerIdx, hints = DEFAULT_HEADER_HINTS) {
  const norm = (aoa || []).map(r => (r || []).map(c => String(c == null ? '' : c).trim()));
  if (!norm.length) return { headers: [], rows: [], headerIndex: 0 };
  const hi = (headerIdx == null || headerIdx < 0 || headerIdx >= norm.length) ? detectHeaderRow(norm, hints) : headerIdx;
  const headerRow = norm[hi] || [];
  const dataRows = norm.slice(hi + 1);
  let width = Math.max(1, ...norm.map(r => r.length));
  while (width > 1) {
    const c = width - 1;
    const headerEmpty = !(headerRow[c] || '').trim();
    const dataEmpty = dataRows.every(r => !(r[c] || '').trim());
    if (headerEmpty && dataEmpty) width -= 1; else break;
  }
  const headers = [];
  for (let i = 0; i < width; i++) headers.push(headerRow[i] || `Column ${i + 1}`);
  const rows = dataRows
    .filter(r => r.slice(0, width).some(c => c !== ''))
    .map(r => { const p = r.slice(0, width); while (p.length < width) p.push(''); return p; });
  return { headers, rows, headerIndex: hi };
}

// Choose the most table-like sheet in a multi-sheet workbook: highest header-
// keyword score, then most data rows, then first. Lets a workbook default to its
// real data sheet (e.g. skip a cover/legend sheet) while the user can override.
export function pickBestSheet(wb, sheetNames, hints = DEFAULT_HEADER_HINTS) {
  let best = sheetNames[0] || '';
  let bestScore = -1;
  for (const name of sheetNames) {
    const aoa = sheetToAoa(wb, name);
    if (!aoa.length) continue;
    const hi = detectHeaderRow(aoa, hints);
    const headerHits = headerScore(aoa[hi] || [], hints);
    const dataRows = Math.max(0, aoa.length - hi - 1);
    const score = headerHits * 1000 + Math.min(dataRows, 999);
    if (score > bestScore) { bestScore = score; best = name; }
  }
  return best;
}

// Header-row detection keywords derived from a schema (its fields' labels/keys +
// the generic set), so detection works for any register, not just BOMs.
export function hintsFor(schema) {
  const extra = [];
  for (const f of (schema.fields || [])) {
    extra.push(String(f.key || '').toLowerCase());
    String(f.label || '').toLowerCase().split(/[^a-z0-9]+/).forEach(w => { if (w.length > 2) extra.push(w); });
  }
  return Array.from(new Set([...DEFAULT_HEADER_HINTS, ...extra]));
}

// Build a header → field-key guesser from a schema's per-field `guess` regexes.
// First field whose regex matches wins (so order schema.fields by specificity).
export function makeGuesser(schema) {
  const fields = schema.fields || [];
  return (header) => {
    const h = String(header || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!h) return '';
    for (const f of fields) {
      for (const rx of (f.guess || [])) { if (rx.test(h)) return f.key; }
    }
    return '';
  };
}

// Coerce a raw cell to a field's declared type.
function coerce(raw, type) {
  const s = (raw == null ? '' : String(raw)).trim();
  if (type === 'number') {
    if (s === '') return null;
    const n = parseFloat(s.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  if (type === 'bool') return /^(y|yes|true|1|x|✓|on)$/i.test(s);
  return s; // text / date — date strings pass through; the backend casts them
}

// Build typed records from a column mapping + parsed table + schema.
// Columns mapped to '__extra' are collected into `attributes[]` (unless the
// schema sets keepExtras:false). Rows where every `identifier` field is empty
// are dropped (so trailing/blank rows don't become ghost records).
export function buildRecords(mapObj, parsed, schema) {
  const fieldByKey = {};
  (schema.fields || []).forEach(f => { fieldByKey[f.key] = f; });
  const idxByKey = {};
  const extraCols = [];
  Object.entries(mapObj).forEach(([col, key]) => {
    if (fieldByKey[key]) idxByKey[key] = Number(col);
    else if (key === '__extra') extraCols.push({ col: Number(col), header: parsed.headers[Number(col)] || `Column ${Number(col) + 1}` });
  });
  const idKeys = (schema.fields || []).filter(f => f.identifier).map(f => f.key);
  const keepExtras = schema.keepExtras !== false;

  return parsed.rows
    .map(r => {
      const rec = {};
      (schema.fields || []).forEach(f => {
        const mapped = idxByKey[f.key] != null;
        const raw = mapped ? r[idxByKey[f.key]] : '';
        let v = coerce(raw, f.type);
        if ((v === '' || v == null) && f.default !== undefined) v = f.default;
        rec[f.key] = v;
      });
      if (keepExtras) {
        rec.attributes = extraCols
          .map(({ col, header }) => ({ key: header, value: (r[col] || '').trim() }))
          .filter(a => a.value !== '');
      }
      return rec;
    })
    .filter(rec => idKeys.length === 0 || idKeys.some(k => rec[k] !== '' && rec[k] != null));
}
